// Per-vertex attribute baking for the batched voxel geometry cache: the
// single-voxel box, the part-geometry finisher and the hi-fi sphere/capsule
// primitives. Split out of voxelGeometryCache.ts (P0.1-full).
import type { BlockVoxel } from '@aod/ui/prefabs';
import { VOXEL_SCALE, materialClassOf } from '@aod/ui/prefabs';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

// BoxGeometry produces 24 position vertices (4 per face × 6 faces) for
// per-face normals. Every per-vertex attribute needs to match that count.
const BOX_VERTS = 24;

// High-fidelity mode: chamfered, smooth-normaled voxels (RoundedBoxGeometry,
// 2 segments). Several× the vertex count of a sharp box — behind a toggle.
const HIFI_SEGMENTS = 2;
const HIFI_RADIUS = VOXEL_SCALE * 0.16;

// Defaults match MeshStandardMaterial's roughness=0.5, metalness=0.1.
// Voxels without explicit overrides use these.
export const DEFAULT_ROUGHNESS = 0.5;
export const DEFAULT_METALNESS = 0.1;

// Module-level temporaries — mutated in place, never reallocated.
const _color = new THREE.Color();
const _emissive = new THREE.Color();

/**
 * `teamMode`: 1/0 per voxel for prefabs that mark team voxels; -1 on every
 * vertex for prefabs that don't (the shader then applies the legacy light
 * whole-body faction tint instead).
 */
export function boxForVoxel(
  v: BlockVoxel,
  legacyTint: boolean,
  hiFi = false,
  textured = false,
): THREE.BufferGeometry {
  let box: THREE.BufferGeometry;
  if (hiFi) {
    box = new RoundedBoxGeometry(VOXEL_SCALE, VOXEL_SCALE, VOXEL_SCALE, HIFI_SEGMENTS, HIFI_RADIUS);
  } else {
    box = new THREE.BoxGeometry(VOXEL_SCALE, VOXEL_SCALE, VOXEL_SCALE);
  }
  box.translate(v.x * VOXEL_SCALE, v.y * VOXEL_SCALE, v.z * VOXEL_SCALE);

  _color.set(v.color);
  _emissive.set(v.emissive ?? '#000000');
  const rough = v.roughness ?? DEFAULT_ROUGHNESS;
  const metal = v.metalness ?? DEFAULT_METALNESS;

  const vertCount = box.attributes.position?.count ?? BOX_VERTS;
  const colors = new Float32Array(vertCount * 3);
  const roughness = new Float32Array(vertCount);
  const metalness = new Float32Array(vertCount);
  const emissive = new Float32Array(vertCount * 3);
  const team = new Float32Array(vertCount).fill(legacyTint ? -1 : v.team ? 1 : 0);
  for (let i = 0; i < vertCount; i++) {
    colors[i * 3] = _color.r;
    colors[i * 3 + 1] = _color.g;
    colors[i * 3 + 2] = _color.b;
    roughness[i] = rough;
    metalness[i] = metal;
    emissive[i * 3] = _emissive.r;
    emissive[i * 3 + 1] = _emissive.g;
    emissive[i * 3 + 2] = _emissive.b;
  }
  box.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  box.setAttribute('aRoughness', new THREE.BufferAttribute(roughness, 1));
  box.setAttribute('aMetalness', new THREE.BufferAttribute(metalness, 1));
  box.setAttribute('aEmissive', new THREE.BufferAttribute(emissive, 3));
  box.setAttribute('aTeam', new THREE.BufferAttribute(team, 1));
  // Buildings share this material but are not textured: class 0 is "leave the colour alone".
  box.setAttribute(
    'aMat',
    new THREE.BufferAttribute(
      new Float32Array(vertCount).fill(textured ? materialClassOf(v) : 0),
      1,
    ),
  );
  return box;
}

/**
 * Fill a part geometry's per-vertex attributes from a representative voxel.
 * Same attribute set as boxForVoxel — the merged result must be uniform.
 */
export function finishPartGeometry(
  input: THREE.BufferGeometry,
  v: BlockVoxel,
  legacyTint: boolean,
): THREE.BufferGeometry {
  // Primitives arrive indexed, RoundedBoxGeometry doesn't — mergeGeometries
  // needs one or the other.
  const geo = input.index ? input.toNonIndexed() : input;
  _color.set(v.color);
  _emissive.set(v.emissive ?? '#000000');
  const rough = v.roughness ?? DEFAULT_ROUGHNESS;
  const metal = v.metalness ?? DEFAULT_METALNESS;
  const vertCount = geo.attributes.position?.count ?? 0;
  const colors = new Float32Array(vertCount * 3);
  const roughness = new Float32Array(vertCount);
  const metalness = new Float32Array(vertCount);
  const emissive = new Float32Array(vertCount * 3);
  const team = new Float32Array(vertCount).fill(legacyTint ? -1 : v.team ? 1 : 0);
  for (let i = 0; i < vertCount; i++) {
    colors[i * 3] = _color.r;
    colors[i * 3 + 1] = _color.g;
    colors[i * 3 + 2] = _color.b;
    roughness[i] = rough;
    metalness[i] = metal;
    emissive[i * 3] = _emissive.r;
    emissive[i * 3 + 1] = _emissive.g;
    emissive[i * 3 + 2] = _emissive.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('aRoughness', new THREE.BufferAttribute(roughness, 1));
  geo.setAttribute('aMetalness', new THREE.BufferAttribute(metalness, 1));
  geo.setAttribute('aEmissive', new THREE.BufferAttribute(emissive, 3));
  geo.setAttribute('aTeam', new THREE.BufferAttribute(team, 1));
  geo.setAttribute(
    'aMat',
    new THREE.BufferAttribute(new Float32Array(vertCount).fill(materialClassOf(v)), 1),
  );
  return geo;
}

/** Most common voxel colour in a part (the primitive's body tint). */
function dominantColor(voxels: readonly BlockVoxel[]): BlockVoxel {
  const counts = new Map<string, number>();
  let best = voxels[0]!;
  let bestN = 0;
  for (const v of voxels) {
    const n = (counts.get(v.color) ?? 0) + 1;
    counts.set(v.color, n);
    if (n > bestN) {
      bestN = n;
      best = v;
    }
  }
  return best;
}

/**
 * Smooth sphere spanning a part's voxel bounds (hi-fi heads): organic crown
 * instead of a stack of cubes. Colour from the part's dominant voxel.
 */
export function partSphere(
  voxels: readonly BlockVoxel[],
  legacyTint: boolean,
): THREE.BufferGeometry | null {
  if (voxels.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const v of voxels) {
    minX = Math.min(minX, v.x);
    minY = Math.min(minY, v.y);
    minZ = Math.min(minZ, v.z);
    maxX = Math.max(maxX, v.x + 1);
    maxY = Math.max(maxY, v.y + 1);
    maxZ = Math.max(maxZ, v.z + 1);
  }
  const wS = (maxX - minX) * VOXEL_SCALE;
  const hS = (maxY - minY) * VOXEL_SCALE;
  const dS = (maxZ - minZ) * VOXEL_SCALE;
  const r = Math.max(wS, hS, dS) / 2;
  if (r <= 0) return null;
  const geo = new THREE.SphereGeometry(r, 12, 10);
  geo.scale(wS / (2 * r) || 1, hS / (2 * r) || 1, dS / (2 * r) || 1);
  geo.translate(
    ((minX + maxX) / 2) * VOXEL_SCALE,
    ((minY + maxY) / 2) * VOXEL_SCALE,
    ((minZ + maxZ) / 2) * VOXEL_SCALE,
  );
  return finishPartGeometry(geo, dominantColor(voxels), legacyTint);
}

/**
 * Capsule along a limb part's longest extent (hi-fi arms/legs): one smooth
 * organic limb instead of a chain of cubes.
 */
export function partCapsule(
  voxels: readonly BlockVoxel[],
  legacyTint: boolean,
): THREE.BufferGeometry | null {
  if (voxels.length === 0) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (const v of voxels) {
    minX = Math.min(minX, v.x);
    minY = Math.min(minY, v.y);
    minZ = Math.min(minZ, v.z);
    maxX = Math.max(maxX, v.x + 1);
    maxY = Math.max(maxY, v.y + 1);
    maxZ = Math.max(maxZ, v.z + 1);
  }
  const wS = (maxX - minX) * VOXEL_SCALE;
  const hS = (maxY - minY) * VOXEL_SCALE;
  const dS = (maxZ - minZ) * VOXEL_SCALE;
  // Limbs are vertical by convention; the radius comes from the thin axes.
  const radius = Math.max(Math.min(wS, dS) / 2, VOXEL_SCALE * 0.3);
  const length = Math.max(0, hS - radius * 2);
  const geo = new THREE.CapsuleGeometry(radius, length, 3, 10);
  geo.translate(
    ((minX + maxX) / 2) * VOXEL_SCALE,
    ((minY + maxY) / 2) * VOXEL_SCALE,
    ((minZ + maxZ) / 2) * VOXEL_SCALE,
  );
  return finishPartGeometry(geo, dominantColor(voxels), legacyTint);
}
